// apps/web/src/layouts/components/useWebPushDevice.ts
//
// Orquestra a inscrição Web Push deste navegador/dispositivo: permissão,
// service worker, PushManager subscribe/unsubscribe + POST/DELETE no backend.
// Contrato: docs/contracts/ESCR-web-push-transport.md.
//
// NO-OP seguro quando o navegador não suporta (sem serviceWorker/PushManager)
// ou quando VAPID está desabilitado no backend (publicKey === '').

import { useCallback, useEffect, useState } from 'react';

import {
  urlBase64ToUint8Array,
  useSubscribeWebPush,
  useUnsubscribeWebPush,
  useVapidPublicKey,
} from '@alamo/api';

export type WebPushStatus =
  | 'unsupported' // navegador sem SW/PushManager OU VAPID off no backend
  | 'denied' // permissão bloqueada pelo usuário
  | 'subscribed' // inscrito neste dispositivo
  | 'idle'; // suportado, ainda não inscrito (default/prompt-able)

export interface UseWebPushDeviceResult {
  status: WebPushStatus;
  /** Mutação em andamento (subscribe/unsubscribe). */
  busy: boolean;
  /** true quando o transporte está disponível (suportado + VAPID on). */
  available: boolean;
  /** Liga as notificações neste dispositivo (pede permissão + inscreve). */
  enable: () => Promise<void>;
  /** Desliga as notificações neste dispositivo (DELETE + unsubscribe). */
  disable: () => Promise<void>;
  /** Último erro de fluxo ('denied' | 'error') para feedback de UI. */
  lastError: 'denied' | 'error' | null;
}

function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function useWebPushDevice(): UseWebPushDeviceResult {
  const supported = isPushSupported();

  const { data: publicKey } = useVapidPublicKey({ enabled: supported });
  const subscribeMut = useSubscribeWebPush();
  const unsubscribeMut = useUnsubscribeWebPush();

  // VAPID off no backend (publicKey === '') ⇒ transporte indisponível.
  const available = supported && typeof publicKey === 'string' && publicKey.length > 0;

  const [status, setStatus] = useState<WebPushStatus>(supported ? 'idle' : 'unsupported');
  const [lastError, setLastError] = useState<'denied' | 'error' | null>(null);

  // Sincroniza o status inicial com a permissão + inscrição existente.
  useEffect(() => {
    if (!available) {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setStatus('denied');
      return;
    }
    let cancelled = false;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (cancelled) return;
        setStatus(sub ? 'subscribed' : 'idle');
      })
      .catch(() => {
        if (!cancelled) setStatus('idle');
      });
    return () => {
      cancelled = true;
    };
  }, [available]);

  const enable = useCallback(async (): Promise<void> => {
    if (!available || typeof publicKey !== 'string' || publicKey.length === 0) return;
    setLastError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus('denied');
        setLastError('denied');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));

      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        setLastError('error');
        return;
      }

      await subscribeMut.mutateAsync({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setStatus('subscribed');
    } catch (err) {
      console.error('[web-push] enable failed', err);
      setLastError('error');
    }
  }, [available, publicKey, subscribeMut]);

  const disable = useCallback(async (): Promise<void> => {
    if (!supported) return;
    setLastError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await unsubscribeMut.mutateAsync({ endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setStatus('idle');
    } catch (err) {
      console.error('[web-push] disable failed', err);
      setLastError('error');
    }
  }, [supported, unsubscribeMut]);

  return {
    status,
    busy: subscribeMut.isPending || unsubscribeMut.isPending,
    available,
    enable,
    disable,
    lastError,
  };
}
